import { DUNGEON, SKILLS } from '@somnhold/content';
import { cellAtWorld } from '../grid.ts';
import { hexToWorld, type Hex } from '../hex.ts';
import { dist2, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero, Maze } from '../types.ts';
import { targetable } from '../battle/combat.ts';
import { field, mazeClimb, ropeAt } from '../battle/maze.ts';
import { orderMove } from '../battle/heroes.ts';
import { castSkill } from '../battle/skills.ts';
import { clericCare, useSkills } from './ghost.ts';

/*
 * The Furrows as a careful group plays them (balance runs only): together behind the first hero standing, fighting
 * what comes close (the prowler too, to scare it off); then a chest, a camp or a lever already in sight; the hall
 * once seen; otherwise the nearest crossing next to the mapped part. With the torch low, or nothing left, back to a rope.
 */

const standing = (h: Hero): boolean => h.downTicks === 0;
const at = (h: Hex): { x: number; y: number } => hexToWorld(h.q, h.r);

/** How much torch a bot keeps for the way back (the share of the run left, bp). */
export const MAZE_BOT = { backAtTicks: 90 * 12, fightRange: 3500, fightSteps: 5, backHpBp: 3000 };

const go = (s: BattleState, h: Hero, p: { x: number; y: number }, near = 300): boolean => {
  if (withinRange(h.x, h.y, p.x, p.y, near)) {
    if (h.moving && withinRange(h.x, h.y, p.x, p.y, 200)) h.moving = false;
    return true;
  }
  if (!h.moving || Math.abs(h.destX - p.x) + Math.abs(h.destY - p.y) > 300) orderMove(s, h, p.x, p.y);
  return false;
};

/** Corridor steps from the hero to every cell, through open doors only (one search per decision). */
const reach = (s: BattleState, h: Hero): Int32Array => field(s, [cellAtWorld(s, h.x, h.y)]);
const cellOf = (s: BattleState, p: { x: number; y: number }): number => cellAtWorld(s, p.x, p.y);

/** The nearest of these crossings the hero can walk to now (-1: none): a lever seen through a wall can wait. */
const nearestOf = (s: BattleState, m: Maze, d: Int32Array, nodes: number[]): number => {
  let best = -1;
  let bd = 0x7fffffff;
  for (const n of nodes) {
    const v = d[cellOf(s, at(m.layout.nodes[n] as Hex))] as number;
    if (v >= 0 && v < bd) {
      bd = v;
      best = n;
    }
  }
  return best;
};

/** Crossings not seen yet with a corridor (open now or not) to one that is. */
const frontier = (m: Maze): number[] => {
  const L = m.layout;
  const out = new Set<number>();
  L.edges.forEach((e) => {
    if (e.kind === 'gate' && !m.gates[L.levers.findIndex((l) => L.edges[l.edge] === e)]) return;
    if (m.seen[e.a] && !m.seen[e.b]) out.add(e.b);
    if (m.seen[e.b] && !m.seen[e.a]) out.add(e.a);
  });
  return [...out].sort((a, b) => a - b);
};

/** Nightmares close by the corridors (one across a wall is not a fight). */
const foesNear = (s: BattleState, h: Hero, d: Int32Array): Enemy[] =>
  s.enemies.filter((e) => {
    if (e.hp <= 0 || !targetable(s, e) || !withinRange(h.x, h.y, e.x, e.y, MAZE_BOT.fightRange)) return false;
    const v = d[cellAtWorld(s, e.x, e.y)] as number;
    // The prowler can't be felled: only fight it when it is on top of the party.
    if (e.kind === 'prowler') return v >= 0 && v <= 1;
    return v >= 0 && v <= MAZE_BOT.fightSteps;
  });

const fight = (s: BattleState, h: Hero, foes: Enemy[]): void => {
  let target = foes[0] as Enemy;
  let bd = 0x7fffffff;
  for (const e of foes) {
    const d = dist2(h.x, h.y, e.x, e.y);
    if (d < bd) {
      bd = d;
      target = e;
    }
  }
  useSkills(s, h, target);
  if (h.cls !== 'cleric' && h.skillCd[2] === 0 && foes.length >= 3) castSkill(s, h, { t: 'skill', slot: 2, x: target.x, y: target.y, id: target.id });
  if (h.target !== target.id) {
    h.target = target.id;
    h.forced = true;
    h.moving = false;
    h.path = [];
  }
};

/** Where the party goes next (the leader decides; the rest follow it). */
const errand = (s: BattleState, h: Hero, m: Maze): { x: number; y: number; climb?: boolean } | null => {
  const L = m.layout;
  const d = reach(s, h);
  const ropes = [L.entry, ...L.camps.filter((_, i) => m.camps[i])];
  const home = (): { x: number; y: number; climb: boolean } | null => {
    const r = nearestOf(s, m, d, ropes);
    return r < 0 ? null : { ...at(L.nodes[r] as Hex), climb: true };
  };
  // Back while the torch lasts, or hurt with nobody to mend it.
  if (m.torchAt > 0 && m.torchAt - s.tick < MAZE_BOT.backAtTicks) return home();
  if (s.heroes.filter(standing).every((o) => o.hp * 10000 < o.stats.maxHp * MAZE_BOT.backHpBp)) return home();
  const chests = L.chests.filter((c, i) => m.seen[c] && !m.chests[i]);
  const camps = L.camps.filter((c, i) => m.seen[c] && !m.camps[i]);
  const levers = L.levers.filter((lv, i) => m.seen[lv.node] && !m.gates[i]).map((lv) => lv.node);
  const job = nearestOf(s, m, d, [...chests, ...camps, ...levers]);
  if (job >= 0) return at(L.nodes[job] as Hex);
  if (m.seen[L.hall] && !m.centreWon && (d[cellOf(s, { x: 0, y: 0 })] as number) >= 0) return { x: 0, y: 0 };
  const n = nearestOf(s, m, d, frontier(m));
  if (n >= 0) return at(L.nodes[n] as Hex);
  // Nothing within reach right now: wait for a wall to breathe, unless all is done.
  return frontier(m).length || (m.seen[L.hall] && !m.centreWon) ? null : home();
};

export const mazeBot = (s: BattleState, h: Hero): void => {
  const m = s.dungeon?.maze;
  if (!m || !standing(h) || h.stun > 0) return;
  if (h.cls === 'cleric' && h.skillCd[2] === 0 && s.heroes.some((o) => o.downTicks > 0 && withinRange(h.x, h.y, o.x, o.y, SKILLS.dawn.radius))) castSkill(s, h, { t: 'skill', slot: 2 });
  const fallen = s.heroes.find((o) => o.downTicks > 0 && withinRange(h.x, h.y, o.x, o.y, 4000));
  const foes = foesNear(s, h, reach(s, h));
  if (foes.length) {
    fight(s, h, foes);
    return;
  }
  if (h.target) {
    h.target = 0;
    h.forced = false;
  }
  if (fallen) {
    go(s, h, fallen, DUNGEON.reviveRange - 300);
    return;
  }
  if (h.cls === 'cleric') clericCare(s, h);
  const up = s.heroes.filter(standing);
  const leader = up[0] as Hero;
  if (leader !== h) {
    // Follow the leader, a step behind.
    if (!withinRange(h.x, h.y, leader.x, leader.y, 1500)) go(s, h, leader, 900);
    return;
  }
  if (h.moving && s.tick % 24 !== 0) return;
  const p = errand(s, h, m);
  if (!p) return;
  // Home: by the rope, up it (once the others are there).
  if (go(s, h, p, 250) && p.climb && ropeAt(m, h) >= 0) mazeClimb(s, m, h);
};
